/* eslint-disable camelcase */
import * as eff from 'redux-saga/effects';
import createActionCreators from 'redux-resource-action-creators';

import {
  actionTypes,
  setProfileState,
} from '../actions';

import {
  getProfile,
  getSubscription,
  invoicesSelector,
  currentCardsSelector,
} from '../selectors';

import {
  throwError,
} from './helpers';

import api from '../api';

export default function* paymentsFlow(reportChannel) {
  function* getTransactions() {
    yield eff.put({
      type: actionTypes.SET_PAYMENT_STATE,
      payload: {
        getTransactionsState: 'pending',
      },
    });
    try {
      const transactions = yield eff.call(
        api.getPersonalEntity,
        {
          type: 'transactions',
        },
      );
      yield eff.put({
        type: actionTypes.SET_PAYMENT_STATE,
        payload: {
          transactions,
        },
      });
      yield eff.put({
        type: actionTypes.SET_PAYMENT_STATE,
        payload: {
          getTransactionsState: 'success',
        },
      });
    } catch (e) {
      yield eff.put({
        type: actionTypes.SET_PAYMENT_STATE,
        payload: {
          getTransactionsState: 'failure',
        },
      });
      yield eff.call(throwError, e);
    }
  }

  function* getInvoices({ qs }) {
    yield eff.put({
      type: actionTypes.SET_PAYMENT_STATE,
      payload: {
        getInvoicesState: 'pending',
      },
    });

    try {
      const invoices = yield eff.call(
        api.getInvoices,
        {
          qs,
        },
      );

      yield eff.put({
        type: actionTypes.SET_PAYMENT_STATE,
        payload: {
          invoices,
          getInvoicesState: 'success',
        },
      });
    } catch (e) {
      yield eff.put({
        type: actionTypes.SET_PAYMENT_STATE,
        payload: {
          getInvoicesState: 'failure',
        },
      });
      yield eff.call(throwError, e);
    }
  }

  function* getSubscriptionPlans() {
    yield eff.put({
      type: actionTypes.SET_PAYMENT_STATE,
      payload: {
        getPlansState: 'pending',
      },
    });
    try {
      const plans = yield eff.call(
        api.getSubscriptionPlans,
      );
      yield eff.put({
        type: actionTypes.SET_PAYMENT_STATE,
        payload: {
          plans,
        },
      });
      yield eff.put({
        type: actionTypes.SET_PAYMENT_STATE,
        payload: {
          getPlansState: 'success',
        },
      });
    } catch (e) {
      yield eff.put({
        type: actionTypes.SET_PAYMENT_STATE,
        payload: {
          getPlansState: 'failure',
        },
      });
      yield eff.call(throwError, e);
    }
  }

  function* getCurrentCustomer() {
    yield eff.put({
      type: actionTypes.SET_PAYMENT_STATE,
      payload: {
        getCustomerState: 'pending',
      },
    });
    try {
      const currentCustomer = yield eff.call(
        api.getCustomer,
      );

      if (currentCustomer?.id) {
        const subscription = currentCustomer?.subscriptions?.data[0];

        const currentUserId = yield eff.select(
          getProfile('id'),
        );

        let lastFailedInvoice = null;
        if (!currentUserId && subscription?.id) {
          const res = yield eff.call(
            api.getLastFailedInvoice,
            {
              subscriptionId: subscription.id,
            },
          );

          lastFailedInvoice = res.id ? res : null;
        }

        yield eff.put({
          type: actionTypes.SET_PAYMENT_STATE,
          payload: {
            currentCustomer,
            lastFailedInvoice,
          },
        });
        yield eff.put({
          type: actionTypes.SET_PAYMENT_STATE,
          payload: {
            getCustomerState: 'success',
          },
        });
      } else {
        yield eff.put({
          type: actionTypes.SET_PAYMENT_STATE,
          payload: {
            getCustomerState: 'failure',
          },
        });
      }
    } catch (e) {
      yield eff.put({
        type: actionTypes.SET_PAYMENT_STATE,
        payload: {
          getCustomerState: 'failure',
        },
      });
      yield eff.call(throwError, e);
    }
  }

  function* payFailedInvoice() {
    const {
      invoiceID,
      subscriptionId,
    } = yield eff.select(
      ({ payments }) => ({
        invoiceID: payments.lastFailedInvoice?.id,
        subscriptionId: payments.currentCustomer.subscriptions?.data[0]?.id,
      }),
    );

    if (invoiceID) {
      const res = yield eff.call(
        api.paymentAttempt,
        {
          invoiceID,
          subscriptionId,
          requesting: true,
        },
      );

      yield eff.put({
        type: actionTypes.SET_PAYMENT_STATE,
        payload: {
          lastFailedInvoice: null,
        },
      });

      return res;
    }

    return null;
  }

  function* paymentAttempt({ callbacks }) {
    yield eff.put({
      type: actionTypes.SET_PAYMENT_STATE,
      payload: {
        paymentAttemptState: 'pending',
      },
    });
    try {
      const res = yield payFailedInvoice();

      yield eff.put({
        type: actionTypes.SET_PAYMENT_STATE,
        payload: {
          paymentAttemptState: 'success',
        },
      });

      if (res) {
        // Re-load resources
        yield eff.put(
          reportChannel,
          {
            messageType: 'reloadProfile',
          },
        );

        callbacks?.onSuccess?.(res);
      }
    } catch (e) {
      callbacks.onError?.(e);
      yield eff.put({
        type: actionTypes.SET_PAYMENT_STATE,
        payload: {
          paymentAttemptState: 'failure',
        },
      });
      yield eff.call(throwError, e);
    }
  }

  function* paymentIntentsSetup() {
    yield eff.put({
      type: actionTypes.SET_PAYMENT_STATE,
      payload: {
        setupIntentState: 'pending',
      },
    });
    try {
      const setupIntent = yield eff.call(
        api.setupPaymentIntent,
      );

      yield eff.put({
        type: actionTypes.SET_PAYMENT_STATE,
        payload: {
          setupIntent,
          setupIntentState: 'success',
        },
      });
    } catch (e) {
      yield eff.put({
        type: actionTypes.SET_PAYMENT_STATE,
        payload: {
          setupIntentState: 'failure',
        },
      });
      yield eff.call(throwError, e);
    }
  }

  function* getPaymentMethods({ callbacks }) {
    const actions = createActionCreators('read', {
      resourceType: 'paymentMethods',
      requestKey: 'getPaymentMethods',
    });

    yield eff.put(actions.pending());

    try {
      const resources = yield eff.call(
        api.getPaymentMethods,
      );

      callbacks?.onSuccess?.(resources);

      yield eff.put(actions.succeeded({
        resources,
      }));
    } catch (e) {
      callbacks?.onError?.(e);

      yield eff.put(actions.failed({
        resources: [],
      }));

      yield eff.call(throwError, e);
    }
  }

  function* reloadLibrary() {
    yield eff.delay(1000);

    yield eff.put({
      type: actionTypes.REQUEST_CURRENT_CUSTOMER,
    });

    yield eff.delay(5000);

    // Re-load resources
    yield eff.put(
      reportChannel,
      {
        messageType: 'reloadProfile',
      },
    );
  }

  function* createPaymentMethod({ qs, body, callbacks }) {
    const actions = createActionCreators('create', {
      resourceType: 'paymentMethods',
      requestKey: 'create',
    });

    try {
      const response = yield eff.call(
        api.createPaymentMethod,
        {
          qs,
          body,
        },
      );

      if (qs?.continue_subscription) {
        yield eff.fork(reloadLibrary);
      }

      yield eff.put(actions.succeeded({
        resources: [response],
      }));

      callbacks?.setSubmitting?.(false);
      callbacks?.onSuccess?.(response);
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));

      callbacks?.setSubmitting?.(false);
      callbacks?.onError?.(e);
      yield eff.call(throwError, e);
    }
  }

  function* deletePaymentMethod({ id, callbacks }) {
    const actions = createActionCreators('delete', {
      resourceType: 'paymentMethods',
      requestKey: 'delete',
    });

    yield eff.put(actions.pending());

    try {
      const response = yield eff.call(
        api.deletePaymentMethod,
        {
          id,
        },
      );

      yield eff.put(actions.succeeded({
        resources: [response],
      }));

      callbacks?.setSubmitting?.(false);
      callbacks?.onSuccess?.(response);
    } catch (e) {
      callbacks?.setSubmitting?.(false);
      callbacks?.onError?.(e);

      yield eff.put(actions.failed({
        resources: [],
      }));

      yield eff.call(throwError, e);
    }
  }

  function* setDefaultPaymentMethod({ id, callbacks }) {
    const actions = createActionCreators('update', {
      resourceType: 'paymentMethods',
      requestKey: 'setDefault',
    });

    yield eff.put(actions.pending());

    try {
      const response = yield eff.call(
        api.defaultPaymentMethod,
        {
          id,
        },
      );

      yield eff.put({
        type: actionTypes.SET_PAYMENT_STATE,
        payload: {
          currentCustomer: response,
        },
      });

      yield eff.put(actions.succeeded({
        resources: [],
      }));

      callbacks?.setSubmitting?.(false);
      callbacks?.onSuccess?.(response);
    } catch (e) {
      callbacks?.setSubmitting?.(false);
      callbacks?.onError?.(e);

      yield eff.put(actions.failed({
        resources: [],
      }));

      yield eff.call(throwError, e);
    }
  }

  function* setSubscription({ body, callbacks }) {
    yield eff.put({
      type: actionTypes.SET_PAYMENT_STATE,
      payload: {
        setPlanState: 'pending',
      },
    });
    try {
      const subscription = yield eff.call(
        api.setSubscription,
        {
          body,
        },
      );

      yield eff.put({
        type: actionTypes.SET_SUBSCRIPTION,
        payload: {
          subscription,
        },
      });
      yield eff.put({
        type: actionTypes.SET_PAYMENT_STATE,
        payload: {
          setPlanState: 'success',
        },
      });

      const settings = yield eff.select(
        getProfile('user_settings'),
      );

      if (settings) {
        yield eff.put(
          setProfileState({
            user_settings: {
              ...settings,
              max_clients_count: Number(subscription.plan?.metadata?.max_clients_count),
            },
          }),
        );
      }

      const coupon = subscription.discount?.coupon;
      if (coupon) {
        yield eff.put(
          reportChannel,
          {
            messageType: 'notify',
            message: {
              type: 'success',
              key: 'payment.coupon_valid',
              params: {
                coupon: coupon.name || coupon.id,
              },
            },
          },
        );
      }
      yield eff.put({
        type: 'GC_ANALYTICS',
        eventKey: 'set_subscription',
        attrs: {
          subscription,
        },
      });

      callbacks?.setSubmitting?.(false);
      callbacks?.onSuccess?.(subscription);
    } catch (e) {
      callbacks?.setSubmitting?.(false);
      callbacks?.onError?.(e);
      yield eff.put({
        type: actionTypes.SET_PAYMENT_STATE,
        payload: {
          setPlanState: 'failure',
        },
      });
      yield eff.call(throwError, e);
    }
  }

  function* updateSubscription({ id, body, callbacks }) {
    yield eff.put({
      type: actionTypes.SET_PAYMENT_STATE,
      payload: {
        updateSubscriptionState: 'pending',
      },
    });
    try {
      const subscription = yield eff.call(
        api.updateSubscription,
        {
          id,
          body,
        },
      );

      yield eff.put({
        type: actionTypes.UPDATE_SUBSCRIPTION,
        payload: {
          subscription,
        },
      });
      yield eff.put({
        type: actionTypes.SET_PAYMENT_STATE,
        payload: {
          updateSubscriptionState: 'success',
        },
      });

      const isPro = yield eff.select(
        getProfile('is_pro'),
      );

      if (isPro) {
        const settings = yield eff.select(
          getProfile('user_settings'),
        );

        yield eff.put(
          setProfileState({
            user_settings: {
              ...settings,
              max_clients_count: Number(subscription.plan?.metadata?.max_clients_count),
            },
          }),
        );
      }

      const coupon = subscription.discount?.coupon;
      if (coupon) {
        yield eff.put(
          reportChannel,
          {
            messageType: 'notify',
            message: {
              type: 'success',
              key: 'payment.coupon_valid',
              params: {
                coupon: coupon.name || coupon.id,
              },
            },
          },
        );
      }
      yield eff.put({
        type: 'GC_ANALYTICS',
        eventKey: 'changed_subscription',
        attrs: {
          subscription,
        },
      });

      callbacks?.setSubmitting?.(false);
      callbacks?.onSuccess?.(subscription);
    } catch (e) {
      callbacks?.setSubmitting?.(false);
      callbacks?.onError?.(e);
      yield eff.put({
        type: actionTypes.SET_PAYMENT_STATE,
        payload: {
          updateSubscriptionState: 'failure',
        },
      });
      yield eff.call(throwError, e);
    }
  }

  function* cancelSubscription({ id, callbacks }) {
    yield eff.put({
      type: actionTypes.SET_PAYMENT_STATE,
      payload: {
        cancelPlanState: 'pending',
      },
    });
    try {
      const subscription = yield eff.call(
        api.cancelSubscription,
        {
          id,
        },
      );

      yield eff.put({
        type: actionTypes.UPDATE_SUBSCRIPTION,
        payload: {
          subscription,
        },
      });
      yield eff.put({
        type: actionTypes.SET_PAYMENT_STATE,
        payload: {
          cancelPlanState: 'success',
        },
      });
      yield eff.put({
        type: 'GC_ANALYTICS',
        eventKey: 'cancel_subscription',
        attrs: {
          subscription,
        },
      });
      callbacks?.setSubmitting?.(false);
      callbacks?.onSuccess?.(subscription);
    } catch (e) {
      callbacks?.setSubmitting?.(false);
      callbacks?.onError?.(e);
      yield eff.put({
        type: actionTypes.SET_PAYMENT_STATE,
        payload: {
          cancelPlanState: 'failure',
        },
      });
      yield eff.call(throwError, e);
    }
  }

  function* addCard({ cardToken, callbacks }) {
    yield eff.put({
      type: actionTypes.SET_PAYMENT_STATE,
      payload: {
        addCardState: 'pending',
      },
    });
    try {
      const card = yield eff.call(
        api.addCard,
        {
          token: cardToken.token.id,
        },
      );

      const { noSaved } = yield eff.select(getSubscription);

      if (noSaved) {
        yield eff.fork(
          setSubscription,
          {
            body: noSaved,
            callbacks: {
              ...callbacks,
              onSuccess: () => {
                callbacks?.onSuccess?.(true);
              },
            },
          },
        );
      } else {
        let isPaid = false;
        const currentUserId = yield eff.select(
          getProfile('id'),
        );

        // if there is no user, then payment is required
        if (!currentUserId) {
          yield payFailedInvoice();
          isPaid = true;

          // Re-load resources
          yield eff.put(
            reportChannel,
            {
              messageType: 'reloadProfile',
            },
          );
        }
        callbacks?.setSubmitting?.(false);
        callbacks?.onSuccess?.(isPaid);
      }

      yield eff.put({
        type: actionTypes.ADD_CARD,
        payload: card,
      });
      yield eff.put({
        type: actionTypes.SET_PAYMENT_STATE,
        payload: {
          addCardState: 'success',
        },
      });
    } catch (e) {
      yield eff.put({
        type: actionTypes.SET_PAYMENT_STATE,
        payload: {
          addCardState: 'failure',
        },
      });

      callbacks?.setSubmitting?.(false);
      callbacks?.onError?.(e);

      yield eff.call(throwError, e);
    }
  }

  function* setDefaultCard({ card }) {
    yield eff.put({
      type: actionTypes.SET_PAYMENT_STATE,
      payload: {
        setDefaultCardState: 'pending',
      },
    });
    try {
      yield eff.call(
        api.setDefaultCard,
        {
          cardId: card.id,
          body: card,
        },
      );
      yield eff.put({
        type: actionTypes.SET_DEFAULT_CARD,
        payload: card.id,
      });
      yield eff.put({
        type: actionTypes.SET_PAYMENT_STATE,
        payload: {
          setDefaultCardState: 'success',
        },
      });
    } catch (e) {
      yield eff.put({
        type: actionTypes.SET_PAYMENT_STATE,
        payload: {
          setDefaultCardState: 'failure',
        },
      });
      yield eff.call(throwError, e);
    }
  }

  function* deleteCard({ cardId }) {
    yield eff.put({
      type: actionTypes.SET_PAYMENT_STATE,
      payload: {
        deleteCardState: 'pending',
      },
    });
    try {
      const deleted = yield eff.call(
        api.deleteCard,
        {
          cardId,
        },
      );
      const cards = yield eff.select(currentCardsSelector);
      const restCards = cards.filter(c => c.id !== deleted.id);
      const defaultCardId = restCards.length ? restCards[0].id : null;

      yield eff.put({
        type: actionTypes.DELETE_CARD,
        payload: {
          defaultCardId,
          deletedId: deleted.id,
        },
      });
      yield eff.put({
        type: actionTypes.SET_PAYMENT_STATE,
        payload: {
          deleteCardState: 'success',
        },
      });
    } catch (e) {
      yield eff.put({
        type: actionTypes.SET_PAYMENT_STATE,
        payload: {
          deleteCardState: 'failure',
        },
      });
      yield eff.call(throwError, e);
    }
  }

  function* createInvoice({ payload, callbacks }) {
    try {
      const invoice = yield eff.call(
        api.createInvoice,
        {
          body: payload,
        },
      );

      const invoices = yield eff.select(
        invoicesSelector,
      );

      yield eff.put({
        type: actionTypes.SET_PAYMENT_STATE,
        payload: {
          invoices: [invoice, ...invoices],
        },
      });

      callbacks?.onSuccess?.(invoice);
    } catch (e) {
      callbacks?.onError?.(e);
      yield eff.call(throwError, e);
    }
  }

  yield eff.takeEvery(actionTypes.REQUEST_INVOICES, getInvoices);
  yield eff.takeEvery(actionTypes.REQUEST_USER_TRANSACTIONS, getTransactions);
  yield eff.takeEvery(actionTypes.REQUEST_SUBSCRIPTION_PLANS, getSubscriptionPlans);
  yield eff.takeEvery(actionTypes.REQUEST_CURRENT_CUSTOMER, getCurrentCustomer);
  yield eff.takeEvery(actionTypes.REQUEST_PAYMENT_ATTEMPT, paymentAttempt);
  yield eff.takeEvery(actionTypes.REQUEST_PAYMENT_INTENTS_SETUP, paymentIntentsSetup);

  yield eff.takeEvery(actionTypes.REQUEST_GET_PAYMENT_METHODS, getPaymentMethods);
  yield eff.takeEvery(actionTypes.REQUEST_CREATE_PAYMENT_METHOD, createPaymentMethod);
  yield eff.takeEvery(actionTypes.REQUEST_DELETE_PAYMENT_METHOD, deletePaymentMethod);
  yield eff.takeEvery(actionTypes.REQUEST_SET_DEFAULT_PAYMENT_METHOD, setDefaultPaymentMethod);

  yield eff.takeEvery(actionTypes.REQUEST_SET_SUBSCRIPTION, setSubscription);
  yield eff.takeEvery(actionTypes.REQUEST_UPDATE_SUBSCRIPTION, updateSubscription);
  yield eff.takeEvery(actionTypes.CANCEL_SUBSCRIPTION_REQUEST, cancelSubscription);
  yield eff.takeEvery(actionTypes.ADD_CARD_REQUEST, addCard);
  yield eff.takeEvery(actionTypes.SET_DEFAULT_CARD_REQUEST, setDefaultCard);
  yield eff.takeEvery(actionTypes.DELETE_CARD_REQUEST, deleteCard);
  yield eff.takeEvery(actionTypes.REQUEST_CREATE_INVOICE, createInvoice);
}
